import AdvisorBackendService from 'AdvisorBackendService';
import AdvisorInvestorContext from 'AdvisorInvestorContext';
import InvestorContext from 'InvestorContext';
import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import FundOpportunityEOI from '../../components/FundOpportunityEOI';
import { useAdvisorAccess } from '../../context/AdvisorAccessContext';
import FundOpportunity from '../../models/FundOpportunity';
import Investor from '../../models/Investor';
import { CommonLoggedInDataContext, useCommonLoggedInData } from 'CommonLoggedInData';
import LoadingSpinner from './LoadingSpinner';
import LookupOpportunityError from './LookupOpportunityError';
import type { LookupOpportunityProps } from './LookupOpportunity';
import { FundUpdate } from './nonAdminLazyImports';
import {
  findInvestorForFundOpportunityFallback,
  resolveInvestorForFundOpportunity,
} from './nonAdminAuthUtils';

export default function LookupOpportunityAdvisor({
  type,
  print,
  printDraft,
}: LookupOpportunityProps) {
  const { id } = useParams();
  const [fundOpportunity, setFundOpportunity] = useState({
    data: null as FundOpportunity | null,
    investor: null as Investor | null,
    content: null as null | string,
  });
  const [investor, setInvestor] = useState<Investor | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loggedInData = useCommonLoggedInData();
  const investorContext = useContext(AdvisorInvestorContext);
  const { investors, loading } = investorContext;
  const { advisedFor } = useAdvisorAccess();

  useEffect(() => {
    let cancelled = false;
    setError(null);

    const lookup = resolveInvestorForFundOpportunity(investors, id, loading(), {
      requireAdvisorContext: true,
      hasAdvisorContext: !!advisedFor,
    });

    if (!lookup.stillLoading) {
      if (lookup.investor) {
        setInvestor(lookup.investor);
        setFundOpportunity({
          data: lookup.investor.fundOpportunity!,
          investor: lookup.investor,
          content: null,
        });
      } else if (lookup.error === 'Fund opportunity not found or not accessible.' && id) {
        // See LookupOpportunity.tsx — closed funds aren't in the cached list;
        // fall back to a targeted, status-agnostic fetch.
        findInvestorForFundOpportunityFallback(new AdvisorBackendService(), id)
          .then((fallbackInvestor) => {
            if (cancelled) return;
            if (fallbackInvestor) {
              setInvestor(fallbackInvestor);
              setFundOpportunity({
                data: fallbackInvestor.fundOpportunity!,
                investor: fallbackInvestor,
                content: null,
              });
            } else {
              console.warn(`No investor found with fund opportunity ID: ${id}`);
              setError(lookup.error);
            }
          })
          .catch(() => {
            if (!cancelled) {
              setError(lookup.error);
            }
          });
      } else if (lookup.error) {
        if (lookup.error === 'No advisor context available.') {
          console.warn('Advisor not set - advisedFor is null');
        } else if (lookup.error === 'No fund opportunities available.') {
          console.warn('No investors available for user');
        }
        setError(lookup.error);
      }
    }

    return () => {
      cancelled = true;
    };
  }, [id, type, investors, loading, advisedFor]);

  if (loading()) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <LookupOpportunityError message={error} />;
  }

  if (!fundOpportunity.data) {
    return <LoadingSpinner />;
  }

  if (type == 'FundUpdate') {
    if (!investor) {
      return <LoadingSpinner />;
    }

    return (
      <FundUpdate
        fundOpportunity={fundOpportunity.data}
        investor={investor}
        advisor={advisedFor || undefined}
        advisorMode={true}
      />
    );
  }

  const key = `${fundOpportunity.investor!.id}-${fundOpportunity.investor!.updatedAt}`;

  return (
    <AdvisorInvestorContext.Consumer>
      {(advisorInvestorContext) => {
        const investorContextValue = {
          ...advisorInvestorContext,
          unreadUpdatesCount: () => {
            const count = advisorInvestorContext.unreadUpdatesCount();
            return typeof count === 'number' ? count : 10;
          },
        };

        return (
          <InvestorContext.Provider value={investorContextValue}>
            <CommonLoggedInDataContext.Provider value={loggedInData}>
              <FundOpportunityEOI
                print={print}
                printDraft={!!printDraft}
                investorId={fundOpportunity.investor!.id}
                key={key}
                advisorMode={true}
                advisedForName={advisedFor?.name}
              />
            </CommonLoggedInDataContext.Provider>
          </InvestorContext.Provider>
        );
      }}
    </AdvisorInvestorContext.Consumer>
  );
}
